import { ThemeProvider, createTheme } from '@mui/material'
import React, { useMemo, useCallback } from 'react'
import { useSelector } from 'react-redux'
import { selectUser } from '../Redux/userSlice'
import { MRT_ColumnDef, MaterialReactTable, useMaterialReactTable } from 'material-react-table'
import { IoEye } from 'react-icons/io5'
import { MRT_Localization_PT_BR } from 'material-react-table/locales/pt-BR'
import { bgLineGridColorDark, bgLineGridColorLight, ContatoEmpresa, corBgGrid, TablePages } from '../../Utils/Types'
import { Funcoes } from '../../Utils/Funcoes'
import { RiEdit2Fill } from 'react-icons/ri'
import { FaTrashAlt } from 'react-icons/fa'
import { ToolTips } from '../ToolTips/tooltips'

interface RowData {
    nome: string,
    setor: string,
    telefone: string,
    celular: string,
    email: string,
    id: number,
    id_user_insert: number,
    id_user_last_update: number,
    name_user_insert: string,
    name_user_last_update: string,
    dt_cadastro: string,
    dt_last_update: string,
}

type Props = {
    contatos: ContatoEmpresa[],
    editContact: (contato: ContatoEmpresa) => void,
    excluirContact: (contato: ContatoEmpresa) => void,
    auditoriaContact: (contato: ContatoEmpresa) => void,
}

export const GridContatoEmpresa = React.memo(({ contatos, editContact, excluirContact, auditoriaContact }: Props) => {

    let user = useSelector(state => selectUser(state))

    const theme = createTheme({
        palette: {
            mode: user.modeDark,

            background: {
                default:
                    user.modeDark === 'light' ? '#fff' : '#475569',
            },
        },
    })

    const baseBackgroundColor = user.modeDark === 'dark' ? '#475569' : '#fff'

    const colunas = useMemo<MRT_ColumnDef<ContatoEmpresa>[]>(() => [

        { accessorKey: 'nome', header: 'Nome do Contato', size: 200 },
        { accessorKey: 'email', header: 'Email', size: 200 },
        { accessorKey: 'telefone', header: 'Telefone', size: 100, Cell: ({ cell }) => <span>{Funcoes.FormatTelefone(cell.getValue<string>())}</span> },
        { accessorKey: 'celular', header: 'Celular', size: 100, Cell: ({ cell }) => <span>{Funcoes.FormatCelular(cell.getValue<string>())}</span> },
        { accessorKey: 'setor', header: 'Setor', size: 150 },

        {
            header: 'Ações', enableSorting: false, size: 50, muiTableBodyCellProps: { align: 'right' }, muiTableHeadCellProps: { align: 'right' },

            Cell: ((cell: any) => {

                const rowData = cell.row.original as RowData

                const handleAuditoriaClick = () => {
                    const contactAuditoria: ContatoEmpresa = {
                        nome: rowData.nome,
                        setor: rowData.setor,
                        telefone: rowData.telefone,
                        celular: rowData.celular,
                        email: rowData.email,
                        id: rowData.id,
                        id_user_insert: rowData.id_user_insert,
                        id_user_last_update: rowData.id_user_last_update,
                        name_user_insert: rowData.name_user_insert,
                        name_user_last_update: rowData.name_user_last_update,
                        dt_cadastro: rowData.dt_cadastro,
                        dt_last_update: rowData.dt_last_update,
                    }

                    auditoriaContact(contactAuditoria)
                }

                const handleEditClick = () => {
                    const contactEdit: ContatoEmpresa = {
                        nome: rowData.nome,
                        setor: rowData.setor,
                        telefone: rowData.telefone,
                        celular: rowData.celular,
                        email: rowData.email,
                        id: rowData.id,
                    }

                    editContact(contactEdit)
                }

                const handleExcluirClick = () => {

                    const timerExcluir = setTimeout(() => {
                        const contactExcluir: ContatoEmpresa = {
                            nome: rowData.nome,
                            setor: rowData.setor,
                            telefone: rowData.telefone,
                            celular: rowData.celular,
                            email: rowData.email,
                            id: rowData.id,
                        }

                        excluirContact(contactExcluir)

                        clearTimeout(timerExcluir)
                    }, 0)

                }

                return (
                    <div className='flex justify-end w-full'>

                        <button
                            tabIndex={-1}
                            data-tooltip-content="Visualizar Auditoria" data-tooltip-id={'btn-view-' + rowData.id}
                            onClick={handleAuditoriaClick}
                            className={`flex items-center justify-center 
                                        text-slate-200 transition-all duration-300 
                                        hover:bg-slate-700 w-[30px] h-[30px] rounded-md 
                                        bg-slate-400`}
                        >
                            <IoEye size={'20px'} />
                        </button>
                        <ToolTips target={'btn-view-' + rowData.id} bgColor='rgb(148, 163, 184)' />

                        <button
                            tabIndex={-1}
                            data-tooltip-content="Editar Contato" data-tooltip-id={'btn-editar-' + rowData.id}
                            onClick={handleEditClick}
                            className={`flex items-center justify-center 
                                        text-slate-200 transition-all duration-300 
                                        hover:bg-sky-600 w-[30px] h-[30px] rounded-md   
                                        bg-sky-500 ml-2`}>
                            <RiEdit2Fill size={'20px'} />
                        </button>
                        <ToolTips target={'btn-editar-' + rowData.id} bgColor='rgb(14, 165, 233)' />

                        <button
                            tabIndex={-1}
                            data-tooltip-content="Excluir Contato" data-tooltip-id={'btn-excluir-' + rowData.id}
                            onClick={handleExcluirClick}
                            className={`flex items-center justify-center
                                        text-slate-200 transition-all duration-300
                                        hover:bg-red-600 w-[30px] h-[30px] rounded-md 
                                        bg-red-500 ml-2`} >
                            <FaTrashAlt size="15px" />
                        </button>
                        <ToolTips target={'btn-excluir-' + rowData.id} bgColor='rgb(239, 68, 68)' />

                    </div>
                )
            }

            )
        },

    ], []
    )

    const table = useMaterialReactTable({
        columns: colunas,
        data: contatos,
        autoResetPageIndex: false,
        mrtTheme: (theme) => ({
            baseBackgroundColor: baseBackgroundColor,

        }),
        muiTableHeadCellProps: {
            sx: {
                borderBottom: `0.1px solid ${user.modeDark === 'light' ? bgLineGridColorLight : bgLineGridColorDark}`,
                backgroundColor: corBgGrid,
                color: `#fff`,
                '& .MuiTableSortLabel-root': {
                    color: '#fff !important',
                    '&:hover': {
                        color: '#fff !important',
                    },
                    '&.Mui-active': {
                        color: '#fff !important',
                    },
                    '& .MuiTableSortLabel-icon': {
                        color: '#fff !important',
                    },
                    '&.Mui-active .MuiTableSortLabel-icon': {
                        color: '#fff !important',
                    },
                },
                '& .MuiIconButton-root': {
                    color: '#fff',
                    '&:hover': {
                        color: '#fff',
                    },
                },
            },
        },
        muiTableBodyCellProps: {
            sx: {
                borderBottom: `0.1px solid ${user.modeDark === 'light' ? bgLineGridColorLight : bgLineGridColorDark}`
            }
        },
        enableEditing: false,
        enableTopToolbar: false,
        enableColumnFilters: false,
        enableHiding: false,
        enableFilters: false,
        enableMultiSort: false,
        enableFullScreenToggle: false,
        enableDensityToggle: false,
        localization: MRT_Localization_PT_BR,
        paginationDisplayMode: 'pages',
        initialState: {
            pagination: { pageIndex: 0, pageSize: 5 },  // Define 5 elementos por página
        },
        muiPaginationProps: {
            rowsPerPageOptions: [5],  // Apenas uma opção para o seletor: 5
        },
    })

    return (
        <>
            <ThemeProvider theme={theme}>

                <MaterialReactTable table={table} />

            </ThemeProvider>

        </>
    )
})


